<template>
  <div class="flex flex-col gap-2" data-test-id="json-viewer">
    <!-- Toolbar -->
    <div class="flex items-center gap-1 flex-wrap">
      <Button
        :label="copied ? 'Copied' : 'Copy'"
        :icon="copied ? 'pi pi-check' : 'pi pi-copy'"
        variant="text"
        size="small"
        data-test-id="json-viewer-copy-btn"
        @click="copyJson"
      />
      <Button
        v-if="viewMode === 'tree'"
        label="Expand all"
        icon="pi pi-angle-double-down"
        variant="text"
        size="small"
        data-test-id="json-viewer-expand-all-btn"
        @click="setAllExpanded(true)"
      />
      <Button
        v-if="viewMode === 'tree'"
        label="Collapse all"
        icon="pi pi-angle-double-up"
        variant="text"
        size="small"
        data-test-id="json-viewer-collapse-all-btn"
        @click="setAllExpanded(false)"
      />
      <Button
        :label="viewMode === 'tree' ? 'Raw' : 'Tree'"
        :icon="viewMode === 'tree' ? 'pi pi-code' : 'pi pi-sitemap'"
        variant="text"
        size="small"
        data-test-id="json-viewer-mode-toggle-btn"
        @click="toggleViewMode"
      />
    </div>

    <p
      v-if="isEmpty"
      class="text-sm text-surface-500 dark:text-surface-400"
      data-test-id="json-viewer-empty"
    >
      {{ emptyMessage }}
    </p>

    <!-- Raw mode stays available because operators paste payloads into tickets. -->
    <pre
      v-else-if="viewMode === 'raw'"
      class="font-mono text-xs whitespace-pre-wrap break-all text-surface-800 dark:text-surface-100"
      data-test-id="json-viewer-raw"
      >{{ rawText }}</pre
    >

    <div v-else data-test-id="json-viewer-tree">
      <JsonTreeNode
        :node-key="null"
        :value="value"
        path="root"
        :depth="0"
        :max-depth="maxDepth"
        :initial-open-depth="initialOpenDepth"
        :expand-signal="expandSignal"
        :expand-target="expandTarget"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import Button from 'primevue/button'
import JsonTreeNode from '@/components/common/JsonTreeNode.vue'
import { useCopyToClipboard } from '@/composables/useCopyToClipboard'

interface Props {
  value: unknown
  /** How many levels start expanded. 1 shows the root's immediate keys. */
  initialOpenDepth?: number
  /** Hard stop on recursion depth — see the note in JsonTreeNode. */
  maxDepth?: number
  /**
   * Shown instead of a tree when `value` is null or undefined.
   *
   * The default names no record type on purpose. Callers know what the payload
   * hangs off — a submission, a payout, an injection result — and the viewer
   * does not, so a caller-specific sentence belongs at the call site rather
   * than baked in here where the next screen to reuse this inherits the wrong
   * noun.
   */
  emptyMessage?: string
}

const props = withDefaults(defineProps<Props>(), {
  initialOpenDepth: 2,
  maxDepth: 20,
  emptyMessage: 'No payload was recorded.',
})

const { copied, copy } = useCopyToClipboard()

const viewMode = ref<'tree' | 'raw'>('tree')
const expandSignal = ref(0)
const expandTarget = ref(true)

const isEmpty = computed(
  () => props.value === null || props.value === undefined,
)

const rawText = computed(() => {
  try {
    return JSON.stringify(props.value, null, 2)
  } catch {
    // Cyclic structures can't be stringified; the tree still renders them.
    return 'Payload could not be serialized'
  }
})

function toggleViewMode() {
  viewMode.value = viewMode.value === 'tree' ? 'raw' : 'tree'
}

function setAllExpanded(expanded: boolean) {
  expandTarget.value = expanded
  expandSignal.value += 1
}

async function copyJson() {
  await copy(rawText.value)
}
</script>
